import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { hasTrackingConsent, CONSENT_CHANGE_EVENT } from '../../utils/cookieConsent';

/**
 * Captures marketing/analytics attribution (referrer + query params) so the
 * backend can target pixels ("fire on navigation A→B with these query tags").
 *
 * This is tracking data, not "necessary": it exists solely to drive the CMS
 * pixels, which are themselves gated by `hasTrackingConsent()`. So we write it
 * only under the same tracking consent, and clear it on withdrawal — keeping it
 * in lockstep with the pixels it feeds. When there's no consent, nothing is
 * written, so the downstream `X-Original-*` headers (conditional reads in
 * DynamicPageInfo/action.js and forms/buildForm.js) are simply never sent.
 */
const QueryCookieSaver = () => {
    const { search } = useLocation();

    useEffect(() => {
        const apply = () => {
            // No / withdrawn tracking consent: never store attribution, and clear
            // anything captured under a previous consent.
            if (!hasTrackingConsent()) {
                Cookies.remove('x-original-referrer');
                Cookies.remove('x-original-query');
                localStorage.removeItem('x-original-referrer');
                return;
            }

            // Referrer — `document.referrer` is the external landing referrer and
            // is stable across SPA navigation within the session, so it re-captures
            // correctly if consent is granted after the initial load.
            const savedReferrer = Cookies.get('x-original-referrer');
            const currentReferrer = document.referrer;
            if (currentReferrer) {
                if (!savedReferrer) {
                    Cookies.set('x-original-referrer', currentReferrer, { secure: false });
                }
                localStorage.setItem('x-original-referrer', JSON.stringify(currentReferrer));
            }

            // Query params (campaign / UTM markers) for the current route.
            if (search && search.length > 1) {
                const cleanedSearch = search.startsWith('?') ? search.substring(1) : search;
                Cookies.set('x-original-query', cleanedSearch, { expires: 7 });
            } else {
                Cookies.remove('x-original-query');
            }
        };

        apply();

        // Re-run the moment consent changes so granting captures immediately and
        // withdrawing clears immediately — no reload.
        window.addEventListener(CONSENT_CHANGE_EVENT, apply);
        return () => window.removeEventListener(CONSENT_CHANGE_EVENT, apply);
    }, [search]);

    return null;
};

export default QueryCookieSaver;
